import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { AppStorageService } from './app-storage.service';
import { DeviceDiscoveryService, type DiscoveredDevice } from './device-discovery.service';
import { NativeLanDiscoveryService } from './native-lan-discovery.service';
import { PlatformService } from './platform.service';

const DISCOVERY_STORAGE_KEY = 'rf_discovered_devices';

@Injectable({ providedIn: 'root' })
export class NetworkDiscoveryService {
  private readonly storage = inject(AppStorageService);
  private readonly platform = inject(PlatformService);
  private readonly nativeDiscovery = inject(NativeLanDiscoveryService);
  private readonly webDiscovery = inject(DeviceDiscoveryService);

  readonly devices = signal<DiscoveredDevice[]>([]);
  readonly scanning = signal(false);
  readonly error = signal('');
  readonly lastScanAt = signal<number | null>(null);
  readonly hasCachedDevices = computed(() => this.devices().length > 0);

  constructor() {
    void this.hydrate();
  }

  async discover(): Promise<DiscoveredDevice[]> {
    this.scanning.set(true);
    this.error.set('');

    try {
      const devices = this.platform.isNative()
        ? await this.discoverNative()
        : await this.webDiscovery.discover();

      this.devices.set(devices);
      this.lastScanAt.set(Date.now());
      await this.storage.set(DISCOVERY_STORAGE_KEY, devices);
      return devices;
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'No se pudo completar el discovery en LAN.');
      return this.devices();
    } finally {
      this.scanning.set(false);
    }
  }

  private async hydrate(): Promise<void> {
    // Seed a dev hostname in non-production builds for testing in Chrome.
    if (environment.devMdnsHostname) {
      this.webDiscovery.addCandidate(environment.devMdnsHostname);
    }

    const cachedDevices = await this.storage.get<DiscoveredDevice[]>(DISCOVERY_STORAGE_KEY, []);
    this.devices.set(cachedDevices);

    // Re-seed mDNS candidates from previously discovered hostnames so that
    // a page reload does not lose the ability to re-discover the same devices.
    for (const device of cachedDevices) {
      if (device.mdnsHostname) {
        this.webDiscovery.addCandidate(device.mdnsHostname);
      } else if (device.ip?.endsWith('.local')) {
        this.webDiscovery.addCandidate(device.ip);
      }
    }
  }

  /**
   * Register a known mDNS hostname (e.g. derived from BLE device name during
   * onboarding) so the next discover() call probes it directly.
   */
  addCandidate(hostname: string): void {
    this.webDiscovery.addCandidate(hostname);
  }

  async removeDevice(key: string): Promise<void> {
    const updated = this.devices().filter((d) => d.key !== key);
    this.devices.set(updated);
    await this.storage.set(DISCOVERY_STORAGE_KEY, updated);
  }

  private async discoverNative(): Promise<DiscoveredDevice[]> {
    return this.nativeDiscovery.discover();
  }
}
